'use client';

import { TSearchRawResponse, ICommunity, IExclusiveContent } from '@search/services';
import { formattedDateWithDateAgo } from '@ui/components';
import { LoadingSkeleton } from '@ui/components/atoms';
import { searchRoutes } from '@ui/constants';
import {
  Search,
  X,
  Loader2,
  ChevronDown,
  Calendar,
  MessageSquare,
  FileText,
  Reply,
} from 'lucide-react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';

interface DomainSearchProps {
  onSearch?: (params: {
    search_text: string;
    search_domains?: string;
    content_filter?: string;
  }) => void;
  initialSearchQuery?: string;
  initialDomain?: string;
  initialContentFilter?: string;
  isLoading?: boolean;
  searchData: TSearchRawResponse;
  error?: any;
  userType: 'creator' | 'fan';
  loadMorePage: () => void;
  hasMore?: boolean;
}

// Content type filter options
type ContentTypeFilter = 'all' | 'posts' | 'comments' | 'replies';

export const DomainSearch = ({
  onSearch,
  initialSearchQuery = '',
  initialDomain = 'community',
  initialContentFilter = 'all',
  isLoading = false,
  searchData,
  error,
  userType,
  loadMorePage,
  hasMore,
}: DomainSearchProps) => {
  const router = useRouter();
  const params = useSearchParams();

  const [searchText, setSearchText] = useState(initialSearchQuery);
  const [domain, setDomain] = useState(initialDomain);
  const [contentTypeFilter, setContentTypeFilter] = useState<ContentTypeFilter>(
    initialContentFilter as ContentTypeFilter,
  );
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const loadingRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => setSearchText(initialSearchQuery), [initialSearchQuery]);
  useEffect(() => setDomain(initialDomain), [initialDomain]);
  useEffect(
    () => setContentTypeFilter(initialContentFilter as ContentTypeFilter),
    [initialContentFilter],
  );

  // Helper functions
  const getContentTypeIcon = (item: ICommunity | IExclusiveContent) => {
    if (domain === 'exclusive_content') {
      return <FileText className="h-4 w-4 text-blue-500" />;
    }

    // For community items, determine type based on thread_deepness and post property
    if ('post' in item) {
      if (item.post) {
        return <MessageSquare className="h-4 w-4 text-green-500" />;
      } else if (item.thread_deepness === 1) {
        return <Reply className="h-4 w-4 text-orange-500" />;
      } else {
        return <Reply className="h-4 w-4 text-purple-500" />;
      }
    }

    return <MessageSquare className="h-4 w-4 text-gray-500" />;
  };

  const getContentTypeLabel = (item: ICommunity | IExclusiveContent) => {
    if (domain === 'exclusive_content') {
      return 'Content';
    }

    if ('post' in item) {
      if (item.post) {
        return 'Post';
      } else if (item.thread_deepness === 1) {
        return 'Comment';
      } else {
        return 'Reply';
      }
    }

    return 'Community';
  };

  const data = useMemo(() => {
    if (!searchData) return [];

    let items: (ICommunity | IExclusiveContent)[] = [];

    if (domain === 'community' && searchData.community) {
      items = searchData.community;
    } else if (domain === 'exclusive_content' && searchData.exclusive_content) {
      items = searchData.exclusive_content;
    }

    // Apply content type filter for community searches
    if (contentTypeFilter !== 'all' && domain === 'community') {
      items = items.filter((item) => {
        if ('post' in item) {
          if (contentTypeFilter === 'posts') return item.post;
          if (contentTypeFilter === 'comments') return !item.post && item.thread_deepness === 1;
          if (contentTypeFilter === 'replies') return !item.post && item.thread_deepness > 1;
        }
        return true;
      });
    }

    return items;
  }, [searchData, domain, contentTypeFilter]);

  const updateURL = (query: string, selectedDomain: string, filter: string = 'all') => {
    const currentParams = new URLSearchParams(params.toString());

    if (query.trim()) {
      currentParams.set('search_query', query.trim());
    } else {
      currentParams.delete('search_query');
    }

    currentParams.set('search_domain', selectedDomain);
    if (filter !== 'all') {
      currentParams.set('content_filter', filter);
    } else {
      currentParams.delete('content_filter');
    }

    const newURL = `${window.location.pathname}?${currentParams.toString()}`;
    router.push(newURL);
  };

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    const trimmedSearch = searchText.trim();
    if (!trimmedSearch) return;

    updateURL(trimmedSearch, domain, contentTypeFilter);
    onSearch?.({
      search_text: trimmedSearch,
      search_domains: domain,
      content_filter: contentTypeFilter !== 'all' ? contentTypeFilter : undefined,
    });
  };

  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDomain = e.target.value;
    setDomain(newDomain);
    // Reset content filter when switching domains
    setContentTypeFilter('all');
    if (searchText) {
      updateURL(searchText, newDomain, 'all');
    }
  };

  // const handleContentFilterChange = (filter: ContentTypeFilter) => {
  //   setContentTypeFilter(filter);
  //   setIsFilterDropdownOpen(false);

  //   if (searchText) {
  //     updateURL(searchText, domain, filter);
  //     onSearch?.({
  //       search_text: searchText,
  //       search_domains: domain,
  //       content_filter: filter !== 'all' ? filter : undefined,
  //     });
  //   }
  // };

  const clearSearch = () => {
    setSearchText('');
    setContentTypeFilter('all');
    const currentParams = new URLSearchParams(params.toString());
    currentParams.delete('search_query');
    currentParams.delete('content_filter');
    router.push(`${window.location.pathname}?${currentParams.toString()}`);
  };

  const handleItemSelect = (item: ICommunity | IExclusiveContent) => {
    const isCreator = userType === 'creator';
    const builder = searchRoutes[domain];
    if (!builder) {
      console.warn(`No route mapping for searchDomain: ${domain}`);
      return;
    }
    const url = builder(item, isCreator);
    router.push(url);
  };

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (
        entry.isIntersecting &&
        hasMore &&
        !isLoading &&
        !isLoadingMore &&
        data.length > 0 // Only load more if there's existing data
      ) {
        setIsLoadingMore(true);
        loadMorePage();
      }
    },
    [hasMore, isLoading, isLoadingMore, loadMorePage, data?.length],
  );

  // Reset loading more state when new data arrives
  useEffect(() => {
    if (!isLoading) {
      setIsLoadingMore(false);
    }
  }, [isLoading]);

  // Click outside to close filter dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (isFilterDropdownOpen && !(e.target as Element).closest('.content-filter-dropdown')) {
        setIsFilterDropdownOpen(false);
      }
    };

    if (isFilterDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isFilterDropdownOpen]);

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    // Only set up observer if we have data and there might be more
    if (data?.length > 0 && hasMore) {
      observerRef.current = new IntersectionObserver(handleIntersection, {
        root: null, // Use viewport as root
        rootMargin: '50px',
        threshold: 0.1,
      });

      if (loadingRef.current) {
        observerRef.current.observe(loadingRef.current);
      }
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [handleIntersection, data?.length, hasMore]);

  return (
    <div className="w-full max-w-2xl mx-auto px-4 py-4" ref={containerRef}>
      <form onSubmit={handleSearch} className="flex flex-col items-stretch gap-2">
        <div className="flex flex-row items-stretch gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              placeholder="Search..."
              disabled={isLoading}
              className="w-full rounded-md border border-muted bg-background py-3 pl-10 pr-8 text-sm shadow-sm transition focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            {searchText && !isLoading && (
              <button
                type="button"
                onClick={clearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <button
            type="submit"
            disabled={isLoading || !searchText.trim()}
            className="sm:w-auto px-4 py-2 bg-primary text-primary-foreground text-white rounded-sm text-sm font-medium shadow-sm hover:bg-primary/90 focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50 transition">
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              </>
            ) : (
              'Search'
            )}
          </button>
        </div>

        <div className="flex flex-row items-stretch gap-2">
          <select
            value={domain}
            onChange={handleDomainChange}
            disabled={isLoading}
            className="sm:w-40 rounded-sm border border-muted bg-background px-3 py-2 text-sm shadow-sm transition focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50">
            <option value="community">Community</option>
            <option value="exclusive_content">Content</option>
          </select>

          {/* Content Type Filter Dropdown */}
          {/* {domain === 'community' && (
            <div className="relative content-filter-dropdown">
              <button
                type="button"
                onClick={() => setIsFilterDropdownOpen(!isFilterDropdownOpen)}
                className="flex items-center space-x-1 px-3 py-2 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-sm transition-colors border border-gray-200 min-w-[100px]"
              >
                <span>
                  {contentTypeFilter === 'all' && 'All Types'}
                  {contentTypeFilter === 'posts' && 'Posts'}
                  {contentTypeFilter === 'comments' && 'Comments'}
                  {contentTypeFilter === 'replies' && 'Replies'}
                </span>
                <ChevronDown className={`h-3 w-3 transition-transform ${isFilterDropdownOpen ? 'rotate-180' : ''}`} />
              </button>
              
              {isFilterDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-200 rounded-md shadow-lg z-10">
                  {(['all', 'posts', 'comments', 'replies'] as ContentTypeFilter[]).map((type) => (
                    <button
                      key={type}
                      onClick={() => handleContentFilterChange(type)}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-gray-50 first:rounded-t-md last:rounded-b-md ${
                        contentTypeFilter === type ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                      }`}
                    >
                      {type === 'all' && 'All Types'}
                      {type === 'posts' && 'Posts'}
                      {type === 'comments' && 'Comments'}
                      {type === 'replies' && 'Replies'}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )} */}
        </div>
      </form>

      {/* Search Info */}
      <div className="mt-4 text-sm text-muted-foreground">
        {searchText ? (
          <p className="text-xl">
            {isLoading && data?.length === 0 ? 'Searching for' : 'Results for'} &quot;
            <span className="font-medium text-foreground">{searchText}</span>&quot; in{' '}
            <span className="font-medium text-foreground">
              {domain === 'community' ? 'Community' : 'Content'}
            </span>
            {domain === 'community' && contentTypeFilter !== 'all' && (
              <span className="text-sm text-blue-600 ml-2">
                (showing {contentTypeFilter}s only)
              </span>
            )}
            {isLoading && data?.length === 0 && '...'}
          </p>
        ) : (
          <p>Start typing to search...</p>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-800">{error?.message || 'Something went wrong'}</p>
        </div>
      )}

      {/* Results */}
      {data?.length > 0 && (
        <div className="py-2 my-4 space-y-3">
          {data.map((item, index) => (
            <div
              key={`${item.id}-${index}`}
              className="
              cursor-pointer p-6 border border-gray-200 rounded-md min-h-[10rem] bg-white shadow-sm
              hover:shadow-md hover:border-gray-300 hover:scale-[1.01] active:scale-[0.98]
              transition-all duration-200 ease-in-out group
            "
              onClick={() => handleItemSelect(item)}>
              <div className="space-y-4">
                {/* Header with type icon and label */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    {getContentTypeIcon(item)}
                    <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      {getContentTypeLabel(item)}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1 text-xs text-gray-400">
                    <Calendar className="h-3 w-3" />
                    <span>
                      {formattedDateWithDateAgo(
                        domain === 'community' ? item?.created_at : item?.date_of_publish,
                      )}
                    </span>
                  </div>
                </div>

                {/* Title */}
                {item?.name && (
                  <h3
                    className="text-lg font-semibold text-gray-900 leading-tight group-hover:text-primaryHover transition-colors"
                    dangerouslySetInnerHTML={{
                      __html: item.name
                        .replace(
                          /<mark>/g,
                          '<span class="bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded text-lg font-medium">',
                        )
                        .replace(/<\/mark>/g, '</span>'),
                    }}
                  />
                )}

                {/* Description */}
                {item?.description && (
                  <p
                    className="text-xs text-gray-600 leading-relaxed line-clamp-3 group-hover:text-primaryHover transition-colors"
                    dangerouslySetInnerHTML={{
                      __html: item.description
                        .replace(
                          /<mark>/g,
                          '<span class="bg-yellow-100 text-yellow-800 px-1 py-0.5 rounded text-xs font-medium">',
                        )
                        .replace(/<\/mark>/g, '</span>'),
                    }}
                  />
                )}

                {/* Footer with creator info */}
                <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-500 font-medium">
                    {/* by {item.creator_display_name} */}
                    {domain === 'community' ? 'Community' : 'Content'}
                  </span>
                  {domain === 'community' &&
                    'thread_deepness' in item &&
                    item.thread_deepness > 1 && (
                      <span className="text-xs text-purple-600 bg-purple-50 px-2 py-1 rounded-full">
                        {/* Level {item.thread_deepness} */}
                      </span>
                    )}
                </div>
              </div>
            </div>
          ))}

          {/* Loading indicator for infinite scroll - only show when loading more */}
          {hasMore && (
            <div ref={loadingRef} className="py-4">
              {isLoadingMore ? (
                <div className="flex justify-center items-center">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-sm text-gray-500">Loading more...</span>
                </div>
              ) : (
                <div style={{ height: '1px' }} /> // Invisible trigger element
              )}
            </div>
          )}
        </div>
      )}

      {/* Initial loading state */}
      {isLoading && data?.length === 0 && searchText && (
        <div className="py-2 my-4 space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="p-6 border border-gray-200 rounded-lg bg-white">
              <LoadingSkeleton className="h-6 w-3/4 mb-3" />
              <LoadingSkeleton className="h-4 w-full mb-2" />
              <LoadingSkeleton className="h-4 w-2/3" />
            </div>
          ))}
        </div>
      )}

      {/* No Results */}
      {!isLoading && searchText && data?.length === 0 && !error && (
        <div className="mt-6 text-center py-8">
          <Search className="h-12 w-12 mx-auto text-gray-300 mb-3" />
          <h3 className="text-sm font-medium text-gray-900 mb-1">No results found</h3>
          <p className="text-xs text-gray-500">
            {contentTypeFilter !== 'all'
              ? `Try different search terms or remove the "${contentTypeFilter}" filter.`
              : 'Try different search terms or switch domains.'}
          </p>
        </div>
      )}
    </div>
  );
};
